/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, { getCatalogId, getUrlSync, IImageSettings, Image, isChannelTypeB2B } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertProductDimensionTypeToDimensionTypes,
    getFallbackImageUrl,
    getProductUrlSync,
    ObjectExtensions,
    Random,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, IModuleProps, isMobile, VariantType, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { RemoveFromComparisonButtonComponent } from '../product-comparison';
import { ProductComparisonState } from '../product-comparison-state';
import {
    getProductsInPreviewProductComparisonAction,
    GetProductsInPreviewProductComparisonInput,
    IProductComparisonProductData
} from './actions';
import { IProductComparisonPreviewData } from './product-comparison-preview.data';
import { IProductComparisonPreviewProps } from './product-comparison-preview.props.autogenerated';

/**
 * Represents product comparison view props for main product info.
 */
export interface IProductComparisonPreviewProductMainInfo {
    productData: IProductComparisonProductData;
    productNameLink?: string;

    removeFromComparisonButton: React.ReactNode;
    productImage: React.ReactNode;
    productLink: React.ReactNode;
}

/**
 * Represents product comparison view props.
 */
export interface IProductComparisonPreviewViewProps extends IProductComparisonPreviewProps<{}> {
    moduleProps: IModuleProps;
    collapseButton: INodeProps;
    isMainContentReady: boolean;

    hasUserInteractedWithComparison?: boolean;
    isExpanded?: boolean;
    subheaderText?: string;
    productsMainInfo?: IProductComparisonPreviewProductMainInfo[];
    previewGreyPlaceHolders?: React.ReactNode;

    productComparisonLink?: string;
    goToComparisonButton?: React.ReactNode;
    clearAllButton?: React.ReactNode;

    handleCollapseClick?(): void;
    clearAllProductsFromTheComparison?(): void;
    removeProductFromList?(itemId: string): void;
}

/**
 * Extended props for the module including info about the product.
 */
export interface IProductComparisonPreviewState {
    isExpanded?: boolean;
    productsData?: IProductComparisonProductData[];
    greyPlaceHoldersCount?: number;
}

/**
 * Extended props for the module including info about the product.
 */
export interface IProductComparisonPreviewFullProps extends IProductComparisonPreviewProps<IProductComparisonPreviewData> {}

/**
 * Represents the component which renders product comparison table.
 */
export class ProductComparisonPreview extends React.PureComponent<IProductComparisonPreviewFullProps, IProductComparisonPreviewState> {
    public static readonly className = 'ms-product-comparison-preview';

    public static readonly containerClassName = `${ProductComparisonPreview.className}__container`;

    public static readonly mainContainerClassName = `${ProductComparisonPreview.containerClassName}__main-container`;

    public static readonly headerClassName = `${ProductComparisonPreview.mainContainerClassName}__header`;

    public static readonly headerLabelClassName = `${ProductComparisonPreview.headerClassName}__label`;

    public static readonly tableClassName = `${ProductComparisonPreview.mainContainerClassName}__table`;

    public static readonly tableRowClassName = `${ProductComparisonPreview.tableClassName}__product-row`;

    public static readonly footerClassName = `${ProductComparisonPreview.mainContainerClassName}__footer`;

    public static readonly collapseButtonClassName = `${ProductComparisonPreview.headerClassName}__collapse`;

    public static _displayedInstanceId?: string;

    private readonly _instanceId: string;

    private _hasUserInteractedWithComparison: boolean;

    public constructor(props: IProductComparisonPreviewFullProps) {
        super(props);

        this.state = {};

        this._instanceId = `${ProductComparisonPreview.className}-${this.props.id}-${Random.Guid.generateGuid()}`;
        ProductComparisonPreview._displayedInstanceId = this._instanceId;

        this._hasUserInteractedWithComparison = false;
    }

    public componentDidMount(): void {
        if (this._instanceId !== ProductComparisonPreview._displayedInstanceId) {
            return;
        }

        this._loadProducts();
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).listModifiedEvent.subscribe(
            {
                instanceId: this._instanceId,

                /**
                 * Updates the state in case the state for the particular product was changed.
                 */
                handler: () => {
                    this._hasUserInteractedWithComparison = true;
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data downloading.
                    this._loadProducts();
                }
            }
        );
    }

    public componentWillUnmount(): void {
        ProductComparisonState.instance(
            this.props.context.request,
            this.props.data.accountInformation?.result
        ).listModifiedEvent.unsubscribe(this._instanceId);
    }

    public render(): React.ReactNode {
        if (this._instanceId !== ProductComparisonPreview._displayedInstanceId) {
            return null;
        }

        const viewProps: IProductComparisonPreviewViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: ProductComparisonPreview.className,
                id: this._instanceId
            },
            collapseButton: this._renderCollapseButton(),
            isMainContentReady: false,
            hasUserInteractedWithComparison: this._hasUserInteractedWithComparison
        };

        const productData = this._getProdutComparisonDataByCatalogId(this.state.productsData ?? this.props.data.productsData?.result);

        if (!ArrayExtensions.hasElements(productData)) {
            return this.props.renderView(viewProps);
        }

        viewProps.isMainContentReady = true;

        const productsLimit = this._getProductsLimit();
        if (!ObjectExtensions.isNullOrUndefined(productsLimit)) {
            viewProps.subheaderText = this.props.resources.productComparisonPreviewHeaderTextWithLimit
                .replace('{0}', productData.length.toString())
                .replace('{1}', productsLimit.toString());
        }

        viewProps.isExpanded = this.state.isExpanded;
        viewProps.handleCollapseClick = this._handleCollapseClick;
        viewProps.productsMainInfo = this._getProductsMainInfo(productData);
        viewProps.previewGreyPlaceHolders = this._renderGreyPlaceHolders(productData);
        viewProps.productComparisonLink = getUrlSync('product-comparison', this.props.context.actionContext);
        viewProps.clearAllProductsFromTheComparison = this._clearAllProductsFromTheComparison;
        viewProps.removeProductFromList = this._removeProductFromList;
        viewProps.goToComparisonButton = this._renderGoToComparisonButton(viewProps.productComparisonLink);
        viewProps.clearAllButton = this._renderClearAllButton();

        return this.props.renderView(viewProps);
    }
    private _getProdutComparisonDataByCatalogId(productData: IProductComparisonProductData[] | undefined): IProductComparisonProductData[] {
        const catalogId = getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        const productByCatalogId = productData?.filter(product => product.catalogId === catalogId) ?? [];
        return productByCatalogId;
    }
    private async _loadProducts(): Promise<void> {
        const items = ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).items;
        const input = new GetProductsInPreviewProductComparisonInput(items);
        const productData = await getProductsInPreviewProductComparisonAction(input, this.props.context.actionContext);
        const productDataByCatalogId = this._getProdutComparisonDataByCatalogId(productData);
        this.setState({ productsData: productDataByCatalogId });
        this._getGreyPlaceHoldersCount(productDataByCatalogId);
    }

    private _getGreyPlaceHoldersCount(productData: IProductComparisonProductData[]): number | undefined {
        if (productData && ArrayExtensions.hasElements(productData)) {
            const limit = this._getProductsLimit();
            if (limit && this.props.context.app.config.displayGreyPlaceHolders) {
                const placeHolder = limit - productData.length;
                this.setState({ greyPlaceHoldersCount: placeHolder });
                return placeHolder;
            }
        }
        return;
    }

    private _getProductsMainInfo(products: IProductComparisonProductData[]): IProductComparisonPreviewProductMainInfo[] {
        const productImageContainerClassName = `${ProductComparisonPreview.tableRowClassName}__product-image-container`;
        const productImageClassName = `${productImageContainerClassName}__product-image`;
        const productNameClassName = `${ProductComparisonPreview.tableRowClassName}__product-name`;

        return products.map(productData => {
            const product = productData.product;

            let productNameLink: string | undefined;
            if (msdyn365Commerce.isBrowser) {
                const productUrl = getProductUrlSync(product, this.props.context.actionContext);
                const fullUrl = new URL(productUrl, window.location.href);
                if (isChannelTypeB2B(this.props.context.actionContext.requestContext)) {
                    fullUrl.searchParams.set('catalogid', `${productData.catalogId}`);
                }
                product.Dimensions?.map(dimension => {
                    const dimensionValue = dimension.DimensionValue?.Value;
                    if (!StringExtensions.isNullOrWhitespace(dimensionValue)) {
                        const dimensionName = convertProductDimensionTypeToDimensionTypes(dimension.DimensionTypeValue);
                        fullUrl.searchParams.set(dimensionName, dimensionValue!);
                    }
                });
                productNameLink = fullUrl.href;
            }

            return {
                productNameLink,
                productData,
                removeFromComparisonButton: (
                    <RemoveFromComparisonButtonComponent
                        className={`${ProductComparisonPreview.tableRowClassName}__remove`}
                        resources={this.props.resources}
                        itemId={productData.itemId}
                        onClickHandler={this._removeProductFromList}
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        data={{}}
                    />
                ),
                productImage: (
                    <a className={productImageContainerClassName} href={productNameLink}>
                        {this._renderProductImage(productImageClassName, productData.product)}
                    </a>
                ),
                productLink: (
                    <a className={productNameClassName} href={productNameLink}>
                        {productData.product.Name}
                    </a>
                )
            };
        });
    }

    /**
     * Represents product comparison preview view.
     * @returns Elements.
     */
    private _renderGreyPlaceHolders = (productData: IProductComparisonProductData[]): React.ReactNode[] | undefined => {
        const counts = this.state.greyPlaceHoldersCount;
        if (this.props.context.app.config.displayGreyPlaceHolders && counts! > 0) {
            const elements = [];
            for (let index = 0; index < counts!; index++) {
                elements.push(
                    <td className='' key={index}>
                        <div className='placeholder-preview' />
                    </td>
                );
            }
            return elements;
        }
        return;
    };

    /**
     * Removes the product from a list.
     * @param itemId - The id of a product to remove.
     */
    private readonly _removeProductFromList = (itemId: string): void => {
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).removeItemById(itemId);
        this.setState(previousState => {
            return {
                productsData: previousState.productsData?.filter(item => item.itemId !== itemId)
            };
        });
    };

    /**
     * Toggle collapsing.
     */
    private readonly _handleCollapseClick: () => void = () => {
        this.setState(previousState => {
            return {
                isExpanded: !(previousState.isExpanded ?? this._hasUserInteractedWithComparison)
            };
        });
    };

    /**
     * Removes all products from the comparison.
     */
    private readonly _clearAllProductsFromTheComparison = () => {
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).clearAll();
        this.setState({ productsData: [] });
    };

    private _getProductsLimit(): number | undefined {
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        if (viewport === 'xs' || viewport === 'sm') {
            return this.props.context.app.config.productComparisonMobileProductsLimit;
        }
        return this.props.context.app.config.productComparisonDesktopProductsLimit;
    }

    /**
     * Retrieves product image react node.
     * @param className - Class name to use.
     * @param product - Product info.
     * @returns React node.
     */
    private _renderProductImage(className: string, product?: SimpleProduct): React.ReactNode | undefined {
        if (ObjectExtensions.isNullOrUndefined(product)) {
            return undefined;
        }

        const defaultImageSettingQ = 'w=140&h=140&m=6';
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: defaultImageSettingQ, w: 0, h: 0 },
                lg: { q: defaultImageSettingQ, w: 0, h: 0 },
                xl: { q: defaultImageSettingQ, w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <Image
                key={product.Name}
                src={product.PrimaryImageUrl ?? ''}
                fallBackSrc={getFallbackImageUrl(product.ItemId, this.props.context.actionContext.requestContext.apiSettings) ?? ''}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                className={className}
                requestContext={this.props.context.actionContext.requestContext}
            />
        );
    }

    private _renderCollapseButton(): INodeProps {
        const shouldBeExpanded = this.state.isExpanded ?? this._hasUserInteractedWithComparison;
        const headerContainerClassName = `${ProductComparisonPreview.mainContainerClassName}__header_button`;
        let collapseClassName = `${ProductComparisonPreview.collapseButtonClassName}__expanded`;
        if (!shouldBeExpanded) {
            collapseClassName = `${ProductComparisonPreview.collapseButtonClassName}__collapsed`;
        }

        return {
            className: classnames(headerContainerClassName, ProductComparisonPreview.collapseButtonClassName, collapseClassName),
            onClick: this._handleCollapseClick,
            role: 'button',
            tabIndex: 0
        };
    }

    private _renderGoToComparisonButton(productComparisonLink: string | undefined): React.ReactNode {
        return (
            <Button
                className={`${ProductComparisonPreview.footerClassName}__go-to-comparison`}
                href={productComparisonLink}
                title={this.props.resources.productComparisonCompareProductsButtonText}
            >
                {this.props.resources.productComparisonCompareProductsButtonText}
            </Button>
        );
    }

    private _renderClearAllButton(): React.ReactNode {
        return (
            <Button
                className={`${ProductComparisonPreview.footerClassName}__clear-all`}
                title={this.props.resources.productComparisonClearAllProductsButtonText}
                onClick={this._clearAllProductsFromTheComparison}
            >
                {this.props.resources.productComparisonClearAllProductsButtonText}
            </Button>
        );
    }
}

export default ProductComparisonPreview;
